import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
// eslint-disable-next-line import/no-unresolved
import { Widget } from '@uploadcare/react-widget/en-min';
import Effects from 'uploadcare-widget-tab-effects';

import getPreviewText from '@tmap/mmm-core/slate/getPreviewText';
import useView from '@tmap/mmm-core/redux/views/useView';
import isEmptyish from '@tmap/mmm-core/isEmptyish';
import { fromKey, toKey, types } from '@tmap/mmm-core/entityKey';
import { topics } from '@tmap/mmm-core/contributionTopics';
import useThunktank from '@tmap/mmm-core/redux/thunktank/useThunktank';

import { FORM_ERROR } from '@tmap/mmm-style-guide/src/final-form';
import { Field, Form } from '@tmap/mmm-style-guide/src/final-form/react';
import { Accordion } from '@tmap/mmm-style-guide/src/Accordion';
import { AccordionDetails } from '@tmap/mmm-style-guide/src/AccordionDetails';
import { AccordionSummary } from '@tmap/mmm-style-guide/src/AccordionSummary';
import { Alert } from '@tmap/mmm-style-guide/src/Alert';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import {
  Add,
  CalendarTodayIcon,
  Remove,
  ScheduleIcon,
} from '@tmap/mmm-style-guide/src/Icon';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import CircleIcon from '@tmap/mmm-style-guide/src/CircleIcon';

import DatePickerField from '@tmap/mmm-style-guide/src/fields/DatePickerField';
import TimePickerField from '@tmap/mmm-style-guide/src/fields/TimePickerField';
import SlateField from '@tmap/mmm-style-guide/src/fields/SlateField';
import TextFieldField from '@tmap/mmm-style-guide/src/fields/TextFieldField';

import useCurrentUser from '../../../hooks/useCurrentUser';
import {
  getPendingContributions,
  getUploadSignature,
  submitContribution,
  updateContribution,
} from '../../../actions/contributions';
import { getUserProfileById } from '../../../actions/userProfile';

import BlockContent from '../../block-content/index';
import TextToolbar from '../textToolbar';
import ContributionPreviewCard from '../contributionPreview';
import ContributionMediaInput from './contributionMediaInput';
import LocationInput from './locationInput';
import ProfileInputs from './profileInputs';
import AuthorAutocompleteField from './authorAutocomplete';

const CenteredGrid = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const EventDetailIcon = styled(CircleIcon)(({ theme }) => ({
  fontSize: '38px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '21px',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  button: {
    padding: theme.spacing(1, 9.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 6.5),
    },
  },
}));

const SideRail = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  display: 'flex',
  justifyContent: 'flex-end',
  backgroundColor: theme.palette.primary.main,
  padding: theme.spacing(4),
  paddingTop: theme.spacing(7),
}));

const BlockContainer = styled(Box, {
  shouldForwardProp: (p) => p !== 'marginTop',
})(({ theme, marginTop }) => ({
  color: theme.palette.primary.contrastText,
  h6: {
    fontWeight: 700,
  },
  ul: {
    marginTop,
    paddingLeft: theme.spacing(3),
    fontSize: '14px',
    li: {
      marginTop: theme.spacing(1.5),
    },
  },
}));

const AccordionContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
  padding: theme.spacing(0.5, 3),
  backgroundColor: theme.palette.primary.main,
  borderRadius: '8px',
  marginTop: theme.spacing(5.5),
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
}));

const MediaContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6.5),
  [theme.breakpoints.down('sm')]: {
    '& .swiper': {
      overflow: 'visible',
    },
  },
}));

const validateMimeTypes = (fileInfo) => {
  if (fileInfo.mimeType && !fileInfo.mimeType.match(/image\/.+|video\/.+/)) {
    throw new Error('type');
  }
};

const AddIcon = styled(Add)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
}));
const RemoveIcon = styled(Remove)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
}));

function PostForm(props) {
  const {
    community,
    existingPost = {},
    postSettings = {},
    isEditing = false,
    pageTitle,
    embedded = false,
    onSubmitSuccess,
  } = props;

  const router = useRouter();
  const user = useCurrentUser();

  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const handleAccordionClick = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const returnUrl = `/moving-living/${community.slug.current}`;
  const successUrl = `/moving-living/${community.slug.current}/thank-you?type=${postSettings?.type || 'default'}${router.query.returnUrl ? `&returnUrl=${router.query.returnUrl}` : ''}`;

  const communityRole = (user.roles ?? []).find((role) => fromKey(role).id === community._id);
  const userIsBlocked = communityRole === toKey(types.BLOCKED, community._id);
  const userIsEmployee = user.roles?.includes('employee');
  const preapproved = !userIsBlocked && (userIsEmployee || Boolean(communityRole));
  const pendingFilters = useMemo(() => {
    const filter = {
      communityId: community._id,
    };
    if (postSettings?.type) {
      filter.type = postSettings.type;
    } else {
      filter.$or = [{ type: 'default' }, { type: null }];
    }
    return filter;
  }, [community._id, postSettings?.type]);
  const [pendingContributionIds] = useView(['pendingContributions', community._id], getPendingContributions(
    user.userId,
    pendingFilters,
  ));

  // only allow the user to create new posts if
  // 1. they're not blocked
  // 2. they don't have any pending posts OR they're already preapproved
  const allowPosting = !userIsBlocked && (preapproved || isEmptyish(pendingContributionIds));

  // uploadcare dialog management
  const useThunk = useThunktank('submitContribution');
  const [contributionSaveState, saveContribution, resetContribution] = useThunk(
    isEditing ? updateContribution : submitContribution,
  );
  const { isLoading, error: contributionError, data } = contributionSaveState;

  const [signatureState, createSignature] = useThunk(getUploadSignature);
  const { data: signatureData, error: signatureError } = signatureState;
  const [uploadProgress, setUploadProgress] = useState(null);
  const [widgetValue, setWidgetValue] = useState(null);
  const handleMediaUpload = ([media], state, { changeValue }) => {
    changeValue(state, 'media', (value) => (value ? [...value, media] : [media]));
  };

  const [authorState, fetchAuthorProfile] = useThunk(getUserProfileById);

  // We don't need to fetch author email unless user is an employee - we'll do that
  // separately so the rest of the post can be preloaded for non-employees
  useEffect(() => {
    if (userIsEmployee && existingPost.authorId) {
      fetchAuthorProfile(existingPost.authorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsEmployee, existingPost.authorId]);

  const widgetApi = useRef();
  useEffect(() => {
    if (signatureData) {
      widgetApi.current.openDialog();
    }
  }, [signatureData]);

  // handles post-submit logic
  useEffect(() => {
    if (data) {
      if (embedded && onSubmitSuccess) {
        onSubmitSuccess(data);
        resetContribution();
      } else {
        router.push(successUrl);
      }
    }
  }, [data, router, successUrl, embedded, onSubmitSuccess, resetContribution]);

  const showProfileFields = isEmptyish(user.firstName) || isEmptyish(user.lastName)
    || isEmptyish(user.profileImage);

  const onAddMediaClick = useCallback(() => createSignature(), [createSignature]);

  const submitUserAndPost = useCallback((values) => {
    const {
      firstName, lastName, profileImage, ...post
    } = values;
    if (post.event && Object.entries(post.event).length === 0) {
      post.event = null;
    }
    const contentPreview = getPreviewText(post.content, { preserveWordBoundary: false });
    const allContent = [contentPreview, post.location, post.media?.length];
    if (allContent.every((content) => !content)) {
      return { [FORM_ERROR]: 'Description, media, or location are required' };
    }
    const userChanges = { firstName, lastName, profileImage };
    saveContribution(
      {
        ...post,
        type: postSettings?.type || 'default',
      },
      community._id,
      showProfileFields ? userChanges : undefined,
    );
    // per final-form docs, return undefined on success, errors on failure
    return undefined;
  }, [community._id, saveContribution, showProfileFields, postSettings?.type]);

  const initialValues = useMemo(() => ({
    ...existingPost,
    author: existingPost.author
      ? { ...existingPost.author, email: authorState.data?.email }
      : user,
    firstName: user.firstName,
    lastName: user.lastName,
  }), [authorState.data?.email, existingPost, user]);

  const allowedTopics = Object.entries(topics)
    .filter(([id]) => postSettings?.allowedTopics?.includes(id));

  return (
    <Form
      keepDirtyOnReinitialize
      onSubmit={submitUserAndPost}
      mutators={{ handleMediaUpload }}
      initialValues={initialValues}
    >{({
      values,
      form,
      handleSubmit,
      invalid,
      dirtySinceLastSubmit,
      submitError,
    }) => {
      const handleTopicClick = (id) => () => {
        if (values.topics?.includes(id)) {
          form.change('topics', values.topics.filter((t) => t !== id));
        } else {
          form.change('topics', [...(values.topics || []), id]);
        }
      };

      const onMediaSelect = (upload) => {
        upload?.progress((tick) => {
          if (tick.state === 'error') {
            setUploadProgress(null);
          } else {
            setUploadProgress(tick.progress);
          }
        });
        upload?.done((fileInfo) => {
          setWidgetValue('loading');
          const uploadType = fileInfo.mimeType.split('/')[0];
          const { width, height } = fileInfo?.crop
            || fileInfo?.originalVideoInfo?.video
            || fileInfo.originalImageInfo
            || {};
          form.mutators.handleMediaUpload({
            mediaId: fileInfo.uuid,
            url: fileInfo.cdnUrl,
            type: uploadType,
            width,
            height,
          });
          setUploadProgress(null);
          setWidgetValue(null);
        });
      };

      const onRemoveMedia = (index) => () => {
        form.change('media', values.media.filter((v, i) => i !== index));
      };

      return (
        <Grid container sx={{ minHeight: '100vh' }} justifyContent={embedded && 'center'}>
          {!embedded && (
            <SideRail item sm={5} md={4}>
              <Box sx={{ width: '260px' }}>
                {postSettings?.showPreview && (
                  <Box sx={{ mb: 7 }}>
                    <ContributionPreviewCard contribution={values} />
                  </Box>
                )}
                {postSettings?.showSiderailContent && (
                  <BlockContainer>
                    <Typography variant='h6' fontWeight={700}>{postSettings.siderailHeader}</Typography>
                    <BlockContent value={postSettings.siderailBody} linkVariant='inverted' />
                  </BlockContainer>
                )}
              </Box>
            </SideRail>
          )}
          <Grid
            item
            xs={12}
            sm={embedded ? 12 : 7}
            md={embedded ? 10 : 8}
            sx={{ padding: embedded ? 4 : { xs: 3, sm: 7 } }}
          >
            <Box sx={{ maxWidth: embedded ? '100%' : '674px' }}>
              {!embedded && (
                <>
                  <Typography variant='h4' color='textPrimary' fontWeight={700}>{pageTitle}</Typography>
                  {postSettings?.subheader && (
                    <Typography variant='body1' color='textSecondary' sx={{ mt: 1.5 }}>{postSettings.subheader}</Typography>
                  )}
                </>
              )}
              {!allowPosting && (
                <Alert severity='error' sx={{ mt: 2 }}>
                  You are unable to post in this community at this time. If you have a post
                  pending approval, you will be able to post again when that post is approved
                  by a community admin.
                </Alert>
              )}
              {allowPosting && (
                <>
                  {userIsEmployee && (!isEditing || authorState.data) && (
                    <>
                      <Typography variant='subtitle1' fontWeight={500} sx={{ mt: embedded ? 1 : 6.5, mb: 2.5 }}>
                        Choose an Author for your post
                      </Typography>
                      <Field
                        name='author'
                        component={AuthorAutocompleteField}
                        fullWidth
                      />
                    </>
                  )}
                  {postSettings?.allowTopicSelection && (
                    <>
                      <Typography variant='subtitle1' fontWeight={500} sx={{ mt: 6.5, mb: 0.5 }}>
                        {postSettings.topicsHeader ?? 'Select All Post Topics that Apply'}
                      </Typography>
                      <Grid container spacing={2} sx={{ marginTop: 0, maxWidth: '550px' }}>
                        {allowedTopics.map(([id, topic]) => {
                          const isActive = values.topics?.includes(id);
                          return (
                            <Grid item key={id}>
                              <Chip
                                variant={isActive ? 'filled' : 'outlined'}
                                size='medium'
                                label={topic.plural}
                                color={isActive ? 'secondary' : 'default'}
                                sx={{ border: isActive ? '1px solid transparent' : undefined }}
                                onClick={handleTopicClick(id)}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </>
                  )}
                  {signatureError && (
                    <Alert severity='error' sx={{ mt: 6.5 }}>{signatureError.friendly}</Alert>
                  )}
                  {postSettings?.allowMedia && (
                    <MediaContainer>
                      <ContributionMediaInput
                        onAddClick={onAddMediaClick}
                        onRemoveMedia={onRemoveMedia}
                        uploadProgress={uploadProgress}
                        media={values.media}
                      />
                    </MediaContainer>
                  )}
                  <Typography variant='subtitle1' fontWeight={500} marginTop={6.5} marginBottom={2.5}>
                    {postSettings?.postHeadingLabel ?? 'Add Post Details'}
                  </Typography>
                  {postSettings?.showPostTitle && (
                    <Field
                      name='title'
                      component={TextFieldField}
                      fullWidth
                      label='Title (Optional)'
                      sx={{ mb: 2.5 }}
                      parse={(value) => (isEmptyish(value) ? null : value)}
                    />
                  )}
                  <Field
                    name='content'
                    component={SlateField}
                    Toolbar={TextToolbar.hookPaste(form.mutators.handleMediaUpload)}
                    label={postSettings?.postBodyLabel ?? 'Post Description Text'}
                    supportHashtags
                    toolbarPlacement='embedded'
                  />
                  {showProfileFields && (
                    <ProfileInputs
                      userImage={user.profileImage}
                      uploadedImage={values.profileImage}
                    />
                  )}
                  {postSettings?.showLocation && (
                    <>
                      <Box sx={{ mt: 6.5, mb: 2.5 }}>
                        <Typography variant='subtitle1' color='textPrimary' component='span' fontWeight={500}>Add Location Details&nbsp;</Typography>
                        <Typography variant='subtitle1' color='textSecondary' component='span' fontWeight={400}>(Optional)</Typography>
                      </Box>
                      <LocationInput />
                    </>
                  )}
                  {postSettings?.showEventTimes && (
                    <>
                      <Box sx={{ mt: 6.5, mb: 2.5 }}>
                        <Typography variant='subtitle1' color='textPrimary' component='span' fontWeight={500}>Add Event Details&nbsp;</Typography>
                        <Typography variant='subtitle1' color='textSecondary' component='span' fontWeight={400}>(Optional)</Typography>
                      </Box>
                      <Grid container spacing={1}>
                        <CenteredGrid item xs='auto' sx={{ maxHeight: '64px' }}>
                          <EventDetailIcon
                            Icon={CalendarTodayIcon}
                            variant='contained'
                            color='primary'
                            shrinkFactor='8 / 19'
                          />
                        </CenteredGrid>
                        <Grid item xs>
                          <Grid container spacing={2.5}>
                            <Grid item xs={12} sm>
                              <Field
                                name='event.startDate'
                                label='Start Date'
                                fullWidth
                                component={DatePickerField}
                                parse={(value) => (isEmptyish(value) ? null : value)}
                              />
                            </Grid>
                            <CenteredGrid item sx={{ display: { xs: 'none', sm: 'flex' } }}>
                              to
                            </CenteredGrid>
                            <Grid item xs={12} sm>
                              <Field
                                name='event.endDate'
                                label='End Date'
                                fullWidth
                                component={DatePickerField}
                                parse={(value) => (isEmptyish(value) ? null : value)}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid container spacing={1} marginTop={2.5}>
                        <CenteredGrid item xs='auto' sx={{ maxHeight: '64px' }}>
                          <EventDetailIcon
                            Icon={ScheduleIcon}
                            variant='contained'
                            color='primary'
                            shrinkFactor='8 / 19'
                          />
                        </CenteredGrid>
                        <Grid item xs>
                          <Grid container spacing={2.5}>
                            <Grid item xs={12} sm>
                              <Field
                                name='event.startTime'
                                label='Start Time'
                                fullWidth
                                component={TimePickerField}
                                parse={(value) => (isEmptyish(value) ? null : value)}
                              />
                            </Grid>
                            <CenteredGrid item sx={{ display: { xs: 'none', sm: 'flex' } }}>
                              to
                            </CenteredGrid>
                            <Grid item xs={12} sm>
                              <Field
                                name='event.endTime'
                                label='End Time'
                                fullWidth
                                component={TimePickerField}
                                parse={(value) => (isEmptyish(value) ? null : value)}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {contributionError && <Alert severity='error' sx={{ mt: 6.5 }}>{contributionError.friendly}</Alert>}
                  {(!dirtySinceLastSubmit && submitError) && (
                    <Alert severity='error' sx={{ mt: 6.5 }}>{submitError}</Alert>
                  )}
                  <Grid container display='flex' justifyContent='space-between' marginTop={6.5}>
                    <Grid item>
                      {!embedded && (
                        <Button variant='text' sx={{ color: (theme) => theme.palette.text.secondary }} href={returnUrl}>
                          Cancel
                        </Button>
                      )}
                    </Grid>
                    <Grid item>
                      <SubmitButton
                        variant='contained'
                        color='secondary'
                        onClick={handleSubmit}
                        loading={Boolean(isLoading || data)}
                        disabled={Boolean(!dirtySinceLastSubmit && invalid)}
                      >
                        {isEditing ? 'Update Post' : 'Submit Post'}
                      </SubmitButton>
                    </Grid>
                  </Grid>
                  <Widget
                    preloader={null}
                    ref={widgetApi}
                    publicKey={process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY}
                    tabs='file camera url facebook'
                    onFileSelect={onMediaSelect}
                    multiple={false}
                    previewStep
                    // controls widget value on-open, which is unnecessary for our use case
                    value={widgetValue}
                    effects='crop,blur,sharp,grayscale,enhance,flip,rotate'
                    customTabs={{ preview: Effects }}
                    inputAcceptTypes={['image/*', 'video/*']}
                    validators={[validateMimeTypes]}
                    secureSignature={signatureData?.signature}
                    secureExpire={signatureData?.expire}
                  />
                </>

              )}
            </Box>
            {!embedded && (
              <AccordionContainer>
                {postSettings?.showPreview && (
                  <StyledAccordion
                    elevation={0}
                    onChange={handleAccordionClick('preview')}
                    expanded={expandedAccordion === 'preview'}
                    disableGutters
                    sx={{ borderBottom: (theme) => `1px solid ${theme.palette.primary.light}` }}
                  >
                    <AccordionSummary expandIcon={expandedAccordion === 'preview' ? <RemoveIcon /> : <AddIcon />}>
                      <Typography variant='body1' fontWeight={500}>
                        See a Preview
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <ContributionPreviewCard contribution={values} />
                    </AccordionDetails>
                  </StyledAccordion>
                )}
                {(
                  postSettings?.showSiderailContent && (
                    <StyledAccordion
                      elevation={0}
                      onChange={handleAccordionClick('siderail')}
                      expanded={expandedAccordion === 'siderail'}
                      disableGutters
                    >
                      <AccordionSummary expandIcon={expandedAccordion === 'siderail' ? <RemoveIcon /> : <AddIcon />}>
                        <Typography variant='body1' fontWeight={500}>
                          {postSettings.siderailHeader}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <BlockContainer marginTop={0}>
                          <BlockContent value={postSettings.siderailBody} />
                        </BlockContainer>
                      </AccordionDetails>
                    </StyledAccordion>
                  )
                )}
              </AccordionContainer>
            )}
          </Grid>
        </Grid>
      );
    }}
    </Form>
  );
}

export default PostForm;
